/**
 * The time inputs of the hourly estimate's "Time" card (054, ebc4054), shared
 * with the Finalize tab so a local job's final time is entered the same way:
 * InlineInput (the editor's commit-on-blur cell), HoursMinutesInput (h + m
 * boxes, one value in whole minutes), and the card styles.
 */

import React, { useState } from 'react';
import { numberInput } from '../lib/numberInput';

/** 220 → "3 h 40 m". */
export const hmShort = (minutes: number) => `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} m`;

export const cardStyle: React.CSSProperties = { border: '1px solid var(--crm-line)', borderRadius: '8px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 };
export const cardTitle: React.CSSProperties = { fontSize: '10px', fontWeight: 700, color: 'var(--crm-muted)', textTransform: 'uppercase', letterSpacing: '.5px' };
export const fieldLabel: React.CSSProperties = { fontSize: '9px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase' };
export const unitLabel: React.CSSProperties = { fontSize: '10px', color: 'var(--crm-dim)', margin: '0 6px 0 3px' };

/**
 * Hours + minutes, two narrow boxes, one value in whole minutes (054). Minutes
 * over 59 carry into the hours (75 min is 1 h 15); a blank box counts as 0.
 * `minutes` null shows both boxes empty.
 */
export function HoursMinutesInput({ minutes, onCommit }: { minutes: number | null; onCommit: (minutes: number) => void }) {
  const h = minutes == null ? '' : Math.floor(minutes / 60);
  const m = minutes == null ? '' : minutes % 60;
  const part = (raw: string) => Math.max(0, Math.round(Number(raw) || 0));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <InlineInput value={h} type="number" step={1} width="44px" onCommit={(v) => onCommit(part(v) * 60 + (minutes ?? 0) % 60)} />
      <span style={unitLabel}>h</span>
      <InlineInput value={m} type="number" step={1} width="44px" onCommit={(v) => onCommit(Math.floor((minutes ?? 0) / 60) * 60 + part(v))} />
      <span style={unitLabel}>m</span>
    </span>
  );
}

// Inline editable cell — commits on blur / Enter, mirrors QuickEditField's pattern.
export function InlineInput({
  value,
  onCommit,
  type = 'text',
  align = 'left',
  prefix,
  suffix,
  step,
  highlight,
  width,
  placeholder,
  negative,
}: {
  value: string | number;
  onCommit: (v: string) => void;
  type?: string;
  align?: 'left' | 'right';
  prefix?: string;
  /** After the box: "/hr", "%", "/flight". */
  suffix?: string;
  step?: number | string;
  /** Amber text: a value edited on this estimate (a rate that is not the fee's own). */
  highlight?: boolean;
  /** Box width, for narrow fields (2–3 digits); number boxes are 64px otherwise. */
  width?: string;
  placeholder?: string;
  /** A number box that takes a negative (a credit line): keeps the keyboard with a minus key. */
  negative?: boolean;
}) {
  const [draft, setDraft] = useState<string>(String(value ?? ''));
  React.useEffect(() => setDraft(String(value ?? '')), [value]);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '1px', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
      {prefix && <span style={{ color: 'var(--crm-muted)' }}>{prefix}</span>}
      <input
        {...(type === 'number' ? numberInput(step == null ? 1 : Number(step), { negative }) : { type })}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== String(value ?? '') && onCommit(draft)}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        style={{
          width: width ?? (type === 'number' ? '64px' : '100%'),
          minWidth: width ? 0 : '40px',
          // 36px: these are edited at speed, and a 22px box is easy to miss.
          minHeight: '36px',
          background: 'var(--crm-panel-2)',
          border: '1px solid var(--crm-line)',
          borderRadius: '6px',
          color: highlight ? 'var(--crm-amber)' : 'var(--crm-text)',
          fontSize: '12px',
          padding: '6px 8px',
          textAlign: align,
          outline: 'none',
          fontFamily: type === 'number' ? "'DM Mono', monospace" : 'inherit',
        }}
      />
      {suffix && <span style={{ color: 'var(--crm-muted)', marginLeft: '2px' }}>{suffix}</span>}
    </span>
  );
}
